import React from 'react';

import {
  TASKS_GRID_COLUMN_LABEL_COST_PER_HA,
  TASKS_GRID_COLUMN_LABEL_COST_PER_UNIT,
  TASKS_GRID_COLUMN_LABEL_INPUT_NAME,
  TASKS_GRID_COLUMN_LABEL_RATE,
  TASKS_GRID_COLUMN_LABEL_RATE_UNIT,
  TASKS_GRID_COLUMN_LABEL_REQUIRED,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInputStep } from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';
import MergeClassNames from 'utils/MergeClassNames';

import { hasDuplicateStepIds } from './hasDuplicateStepIds';
import { getTaskSteps } from './stepCalculations';
import { filterStepsByCategory } from './stepCategoryFilters';
import { StepSelection } from './stepFactory';
import { getStepFilterDetail } from './taskCaption';
import TaskStepRow from './TaskStepRow';
import type { useStepContextMenu } from './useStepContextMenu';
import type { useStepReorder } from './useStepReorder';

type StepReorderApi = ReturnType<typeof useStepReorder>;
type StepContextMenuApi = ReturnType<typeof useStepContextMenu>;

interface TaskStepsTableProps {
  task: IInputTask;
  activeStepCategoryKey: string;
  gUnits: GUnit[];
  editingStepInputId: string | null;
  setEditingStepInputId: (stepId: string | null) => void;
  handleStepInputChange: (
    task: IInputTask,
    stepId: string,
    selections: StepSelection[]
  ) => void;
  commitStepField: (
    task: IInputTask,
    stepId: string,
    patch: Partial<IInputStep>
  ) => void;
  stepReorder: StepReorderApi;
  stepContextMenu: StepContextMenuApi;
  renderTaskDetailsSection: (task: IInputTask) => React.ReactNode;
  renderInputTableFooter: (
    task: IInputTask,
    hiddenCount: number
  ) => React.ReactNode;
}

// A task's expanded row: the Application Details card, then the step
// sub-table (or, once the current category filter hides every step, just
// the footer explaining that). Steps are filtered by the active category
// tab but re-ordering/dragging always resolves against the full unfiltered
// list, so a filtered-out step keeps its position when it reappears.
const TaskStepsTable: React.FC<TaskStepsTableProps> = ({
  task,
  activeStepCategoryKey,
  gUnits,
  editingStepInputId,
  setEditingStepInputId,
  handleStepInputChange,
  commitStepField,
  stepReorder,
  stepContextMenu,
  renderTaskDetailsSection,
  renderInputTableFooter,
}) => {
  const allSteps = getTaskSteps(task);
  const filteredSteps = filterStepsByCategory(allSteps, activeStepCategoryKey);

  if (allSteps.length === 0) {
    return (
      <div className="input-task-steps-wrapper ml-7 pb-6">
        {renderTaskDetailsSection(task)}
        {renderInputTableFooter(task, 0)}
      </div>
    );
  }

  if (filteredSteps.length === 0) {
    return (
      <div className="input-task-steps-wrapper ml-7 pb-6">
        {renderTaskDetailsSection(task)}
        {renderInputTableFooter(task, allSteps.length)}
      </div>
    );
  }

  // Every part of re-ordering addresses a row by its stepId, so duplicated
  // ids would move the wrong step — the task opts out entirely and the
  // footer says so (OP-2321/OP-2322 follow-up).
  const hasAmbiguousStepIds = hasDuplicateStepIds(allSteps);
  // Nothing to swap with when only one step is visible, so the grip and the
  // Reorder menu item both sit this out. The lane itself is reserved
  // unconditionally, so it never moves.
  const canReorder = filteredSteps.length > 1 && !hasAmbiguousStepIds;
  const isTaskReordering = stepReorder.reorderTarget?.taskId === task.taskId;

  return (
    <div>
      <div className="input-task-steps-wrapper ml-7 pb-6">
        {renderTaskDetailsSection(task)}
        {/* Hosts keyboard focus for reorder mode. It must be this wrapper
            and not the grip: swapping a step downward re-inserts its own
            <tr>, which blurs anything inside it, whereas this element
            never moves. */}
        <div
          ref={stepReorder.registerStepsWrapper(task.taskId)}
          tabIndex={-1}
          onKeyDown={stepReorder.handleReorderKeyDown}
          onBlur={stepReorder.handleReorderBlur}
          // pl-6 reserves the lane the grip handles sit in, between the
          // green rail and the grid itself. It is unconditional, so
          // filtering a task down to a single step can no longer nudge the
          // column (OP-2321 follow-up). Re-order mode turns the arrow keys
          // into a command, and a shifted or dragged pointer would
          // otherwise paint a text selection across the grid while the
          // user works — scoped to the mode, so ordinary selection is
          // untouched.
          className={MergeClassNames(
            'input-task-steps-table border-l-2 pb-4 pl-6 outline-none',
            hasAmbiguousStepIds ? 'border-danger-dark' : 'border-success-dark',
            isTaskReordering && 'select-none'
          )}
        >
          <table className="w-full table-fixed border-collapse text-base text-gray-dark">
            <thead>
              <tr>
                {/* Input name takes the remaining width; the value
                    columns are fixed and sized to their content (OP-2534),
                    so they don't spread apart on a wide screen. */}
                <th className="border-b border-gray-medium px-2 py-1.5 text-left text-sm font-semibold">
                  {TASKS_GRID_COLUMN_LABEL_INPUT_NAME}
                </th>
                <th className="w-[6rem] border-b border-gray-medium px-2 py-1.5 text-left text-sm font-semibold">
                  {TASKS_GRID_COLUMN_LABEL_RATE}
                </th>
                <th className="w-[7rem] border-b border-gray-medium px-2 py-1.5 text-left text-sm font-semibold">
                  {TASKS_GRID_COLUMN_LABEL_RATE_UNIT}
                </th>
                <th className="w-[7rem] border-b border-gray-medium px-2 py-1.5 text-left text-sm font-semibold">
                  {TASKS_GRID_COLUMN_LABEL_COST_PER_UNIT}
                </th>
                <th className="w-[7rem] border-b border-gray-medium px-2 py-1.5 text-left text-sm font-semibold">
                  {TASKS_GRID_COLUMN_LABEL_REQUIRED}
                </th>
                {/* Right-aligned to match the task-level $/ha total below
                    this table — both are currency values, so digits line
                    up the same way in both places. */}
                <th className="w-[7rem] border-b border-gray-medium px-2 py-1.5 text-right text-sm font-semibold">
                  {TASKS_GRID_COLUMN_LABEL_COST_PER_HA}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredSteps.map((step, index) => (
                <TaskStepRow
                  key={
                    hasAmbiguousStepIds
                      ? `${step.stepId}:${index}`
                      : step.stepId
                  }
                  task={task}
                  step={step}
                  isLastRow={index === filteredSteps.length - 1}
                  detailLine={getStepFilterDetail(step, activeStepCategoryKey)}
                  hasAmbiguousStepIds={hasAmbiguousStepIds}
                  canReorder={canReorder}
                  gUnits={gUnits}
                  editingStepInputId={editingStepInputId}
                  setEditingStepInputId={setEditingStepInputId}
                  handleStepInputChange={handleStepInputChange}
                  commitStepField={commitStepField}
                  stepReorder={stepReorder}
                  stepContextMenu={stepContextMenu}
                />
              ))}
            </tbody>
          </table>
        </div>
        {renderInputTableFooter(task, allSteps.length - filteredSteps.length)}
      </div>
    </div>
  );
};

export default TaskStepsTable;
